import type { JSX } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import TuneIcon from '@mui/icons-material/Tune';

export interface SignupResultProps {
  tenantId: string;
  launcherUrl: string;
  isNew: boolean;
  onSignupAnother: () => void;
  onCustomize: () => void;
}

/**
 * Welcome card shown after a successful /signup. The launcher URL is the primary
 * takeaway — that's what the customer pastes into their MDM's managed-browser
 * kiosk config, or sideloads alongside the APK.
 */
export function SignupResult({
  tenantId,
  launcherUrl,
  isNew,
  onSignupAnother,
  onCustomize,
}: SignupResultProps): JSX.Element {
  return (
    <Card variant="outlined" data-testid="signup-result">
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="h6" fontWeight={700}>
            {isNew ? 'Welcome aboard' : 'You already have an account'}
          </Typography>
          <Chip size="small" label="Core (free)" />
        </Stack>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          {isNew ? 'Your launcher is ready to deploy.' : 'Using the same email returns your existing launcher.'}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary">Tenant ID</Typography>
          <Typography sx={{ fontFamily: 'monospace', mb: 1 }}>{tenantId}</Typography>
          <Typography variant="overline" color="text.secondary">Launcher URL</Typography>
          <Box>
            <Link href={launcherUrl} target="_blank" rel="noopener noreferrer" data-testid="signup-launcher-url" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {launcherUrl}
            </Link>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="overline" color="text.secondary">What&apos;s next</Typography>
        <List dense>
          <ListItem disableGutters>
            <ListItemText primary="Open your launcher in any browser using the URL above — you'll see the starter config we seeded." />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText primary="Deploy via your MDM (SOTI MobiControl, Intune, Workspace ONE, Scalefusion, Hexnode, Esper) or sideload the standalone APK. The Devices section generates a deployment pack." />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText primary="Upgrade to Pro/Enterprise to unlock theming, multi-profile, scheduling, broadcasts, the offline bundle, remote config, and telemetry." />
          </ListItem>
        </List>

        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={<TuneIcon />} onClick={onCustomize} data-testid="signup-customize">
            Customize my launcher
          </Button>
          <Button variant="outlined" startIcon={<LaunchIcon />} href={launcherUrl} target="_blank" rel="noopener noreferrer">
            Open my launcher
          </Button>
          <Button onClick={onSignupAnother}>Sign up another fleet</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
